import * as Sentry from "@sentry/react";
import { useEffect } from "react";
import {
  Navigate,
  Outlet,
  Route,
  Routes,
  useSearchParams,
} from "react-router-dom";
import { useIntercom } from "react-use-intercom";

import BaseLayout from "common/components/BaseLayout";
import EmptyPlaceholder from "common/components/EmptyPlaceholder";
import { Button } from "common/components/ui/Button";
import WorkspaceLayoutContainer from "common/components/WorkspaceLayoutContainer";
import { useAuth } from "common/context/AuthContext";
import useInvitations from "common/datahooks/useInvitations";
import useWorkspaces from "common/datahooks/useWorkspaces";
import { getWhitelabelCustomization } from "common/helpers/utils";
import useAppInit from "common/hooks/useAppInit";
import useExternalCouponState from "common/hooks/useExternalCouponState";
import useTwBreakpoint from "common/hooks/useTwBreakpoint";

import CrashPlaceholder from "./common/components/CrashPlaceholder";
import useCustomer from "./common/datahooks/useCustomer";
import Invitation from "./pages/invitation/components/Invitation";
import Login from "./pages/login/Login";
import NoWorkspaces from "./pages/no-workspaces/components/NoWorkspaces";
import Questionnaire from "./pages/questionnaire/components/Questionnaire";
import { workspaceRoutes } from "./pages/routes";
import Workspaces from "./pages/workspaces/components/Workspaces";

import PlaceholderImage from "assets/images/empty-placeholders/maintenance.png";

const isMaintenance = false;

function App() {
  useAppInit();
  useExternalCouponState();
  const isDesktop = useTwBreakpoint("lg");
  const { isAuthorized } = useAuth();
  const { customerInfo } = useCustomer();
  const { workspaces } = useWorkspaces();
  const { invitations } = useInvitations();
  const { show } = useIntercom();
  const [searchParams] = useSearchParams();

  const isAppLoaded =
    (getWhitelabelCustomization() && isAuthorized === false) ||
    (isAuthorized === true && customerInfo && workspaces && invitations);

  useEffect(() => {
    if (isAppLoaded || isMaintenance) {
      document.getElementById("loader-container")?.remove();
    }
  }, [isAppLoaded, isMaintenance]);

  if (isMaintenance) {
    return (
      <main className="flex h-dvh grow items-center justify-center p-6">
        <EmptyPlaceholder
          title="Maintenance in Progress"
          subtitle="We're performing essential updates to improve your dashboard. Thanks for your patience—we'll be back shortly!"
          imageSrc={PlaceholderImage}
          actionComponent={
            <div className="flex flex-col lg:gap-y-2.5 lg:mt-1 gap-y-4">
              <span className="text-sm lg:text-xxs text-black-500 text-center">
                Need help in the meantime?
              </span>
              <Button
                onClick={show}
                variant="secondary-purple"
                size={isDesktop ? "sm" : "lg"}
              >
                Chat with support
              </Button>
            </div>
          }
        />
      </main>
    );
  }

  if (!isAppLoaded) {
    return null;
  }

  let routes: JSX.Element;

  if (getWhitelabelCustomization()) {
    if (isAuthorized) {
      if (workspaces.length) {
        if (workspaces.length > 1) {
          routes = (
            <>
              <Route
                path="workspaces/:workspaceId"
                element={<WorkspaceLayoutContainer />}
              >
                {workspaceRoutes.map(({ path, component }) => (
                  <Route key={path} path={path} element={component} />
                ))}
                {/* since we don't have an index route atm, this redirects index route to first workspace route */}
                {/* e.g.: xxx/workspaces/1aa4c82b-cdf8-417e-bcda-966d661f981a/ */}
                <Route
                  path=""
                  element={<Navigate to={workspaceRoutes[0].path} replace />}
                />
              </Route>
              <Route
                path="*"
                element={
                  <Navigate
                    to={`workspaces/${workspaces[0].id}/${workspaceRoutes[0].path}?${searchParams.toString()}`}
                    replace
                  />
                }
              />
            </>
          );
        } else {
          routes = (
            <>
              <Route path="/" element={<WorkspaceLayoutContainer />}>
                {workspaceRoutes.map(({ path, component }) => (
                  <Route key={path} path={path} element={component} />
                ))}
                {/* since we don't have an index route atm, this redirects index route to first workspace route */}
                <Route
                  path=""
                  element={<Navigate to={workspaceRoutes[0].path} replace />}
                />
              </Route>
              <Route
                path="*"
                element={
                  <Navigate
                    to={`/${workspaceRoutes[0].path}?${searchParams.toString()}`}
                    replace
                  />
                }
              />
            </>
          );
        }
      } else {
        routes = (
          <>
            <Route
              path="/no-workspaces"
              element={
                <BaseLayout>
                  <Outlet />
                </BaseLayout>
              }
            >
              <Route path="" element={<NoWorkspaces />} />
            </Route>
            <Route path="*" element={<Navigate to="no-workspaces" replace />} />
          </>
        );
      }
    } else {
      routes = (
        <>
          <Route path="/login" element={<Login />} />
          <Route path="*" element={<Navigate to="/login" replace />} />
        </>
      );
    }
  } else {
    const inviteToken = searchParams.get("invite_token");
    const hasInvitation = !!(inviteToken || invitations.length);

    // By default, redirect to the first workspace
    let baseRoutesRedirectTo: string;
    let workspaceRoutesRedirectTo: string;

    if (hasInvitation) {
      baseRoutesRedirectTo = `/invitations/${inviteToken || invitations[0].token}`;
    } else if (customerInfo.show_questionnaire) {
      baseRoutesRedirectTo = "/questionnaire";
    } else if (!workspaces.length) {
      baseRoutesRedirectTo = "/no-workspaces";
      workspaceRoutesRedirectTo = "/no-workspaces";
    } else {
      const firstWorkspaceRedirectTo = `workspaces/${workspaces[0].id}/${workspaceRoutes[0].path}?${searchParams.toString()}`;
      baseRoutesRedirectTo = firstWorkspaceRedirectTo;
      workspaceRoutesRedirectTo = firstWorkspaceRedirectTo;
    }

    routes = (
      <>
        {/* Base Routes */}
        <Route
          element={
            <BaseLayout>
              <Outlet />
            </BaseLayout>
          }
        >
          <Route path="invitations/:inviteToken" element={<Invitation />} />
          {customerInfo.show_questionnaire && (
            <Route path="questionnaire" element={<Questionnaire />} />
          )}
          {workspaces.length ? (
            <Route path="workspaces" element={<Workspaces />} />
          ) : (
            <Route path="no-workspaces" element={<NoWorkspaces />} />
          )}
          <Route
            path="*"
            element={<Navigate to={baseRoutesRedirectTo} replace />}
          />
        </Route>

        {/* Workspace routes */}
        {!customerInfo.show_questionnaire &&
          !hasInvitation &&
          workspaces.length && (
            <>
              <Route
                path="workspaces/:workspaceId"
                element={<WorkspaceLayoutContainer />}
              >
                {workspaceRoutes.map(({ path, component }) => (
                  <Route key={path} path={path} element={component} />
                ))}
              </Route>
              <Route
                path="*"
                element={<Navigate to={workspaceRoutesRedirectTo} replace />}
              />
            </>
          )}
      </>
    );
  }

  return (
    <Sentry.ErrorBoundary
      fallback={({ resetError, eventId }) => (
        <CrashPlaceholder resetError={resetError} eventId={eventId} />
      )}
    >
      <Routes>{routes}</Routes>
    </Sentry.ErrorBoundary>
  );
}

export default App;
